import { ChangeDetectionStrategy, Component, Input, SimpleChanges } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { SelectModule } from 'primeng/select'
import { ButtonModule } from 'primeng/button'
import { ButtonGroupModule } from 'primeng/buttongroup'

import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { CalendarViewService } from '../../services/calendar-view.service'
import { Subject, takeUntil } from 'rxjs'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { DatePickerModule } from 'primeng/datepicker'
import { CommonModule } from '@angular/common'

interface NumDaysSelections {
    label: string
    value: number
}

// TODO: Replace with design system token when available
const CALENDAR_BUTTONS_TOKEN = {
    root: {
        borderRadius: '8px',
    },
    secondary: {
        background: '#fff',
        color: '#62647E',
        activeBackground: '#f2e8dc',
        borderColor: '#f2e8dc',
    },
    sm: {
        fontSize: '12px',
        fontWeight: 600,
    },
}

const DAYS_DROP_DOWN_TOKEN = {
    root: {
        borderRadius: '8px',
    },
}

const DATE_PCIKER_TOKEN = {
    focusRing: {
        width: 0,
        shadow: 0,
        offset: 0,
    },
}

@Component({
    selector: 'fm-calendar-header',
    imports: [
        CommonModule,
        DatePickerModule,
        FormsModule,
        SelectModule,
        ButtonModule,
        ButtonGroupModule,
        FontAwesomeModule,
    ],
    templateUrl: './calendar-header.component.html',
    styleUrl: './calendar-header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarHeaderComponent {
    @Input() timezone: string
    @Input() scrollBarWidth: number = 15
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    public dateStringDisplay: string
    public date = new Date()

    faChevronLeft = faChevronLeft
    faChevronRight = faChevronRight
    calendarButtonToken = CALENDAR_BUTTONS_TOKEN
    daysDropdownToken = DAYS_DROP_DOWN_TOKEN
    datePickerToken = DATE_PCIKER_TOKEN

    public numberOfDaysOptions: NumDaysSelections[] = [
        { label: '1 day', value: 1 },
        { label: '2 days', value: 2 },
        { label: '3 days', value: 3 },
        { label: '5 days', value: 5 },
        { label: '7 days', value: 7 },
    ]
    public selectedNumberOfdays: number

    constructor(private calendarViewService: CalendarViewService) {}

    onSelectDate(event) {
        this.processDateSelected()
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['timezone'] && !changes['timezone'].previousValue) {
            this.initializeDate()
        }
    }

    initializeDate() {
        if (this.timezone) {
            const momentNowInTimezone = moment.tz(moment(), this.timezone)
            this.date = momentNowInTimezone.toDate()
            this.processDateSelected()
        }
    }

    ngOnInit() {
        this.initializeDate()

        this.calendarViewService.numberOfDaysToShow$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((numberOfDays) => {
                this.selectedNumberOfdays = numberOfDays
            })
    }

    processDateSelected() {
        const timezoneMoment = moment(this.date).tz(this.timezone)
        this.dateStringDisplay = timezoneMoment.format('MMM YYYY')
        this.calendarViewService.setStartTimeForCalendar(timezoneMoment.startOf('day').valueOf())
    }

    onClickToday() {
        const momentNowInTimezone = moment.tz(moment(), this.timezone)
        this.date = momentNowInTimezone.toDate()
        this.processDateSelected()
    }

    onClickAdvanceForward() {
        this.date = this.addDays(this.selectedNumberOfdays)
        this.processDateSelected()
    }

    onClickAdvanceBackwards() {
        this.date = this.subtractDays(this.selectedNumberOfdays)
        this.processDateSelected()
    }

    onChangeNumberOfDaysToShow(event) {
        this.calendarViewService.setNumberOfDaysToShow(event.value)
    }

    private addDays(days: number): Date {
        const newDate = new Date(this.date)
        newDate.setDate(newDate.getDate() + days)
        return newDate
    }

    private subtractDays(days: number): Date {
        const newDate = new Date(this.date)
        newDate.setDate(newDate.getDate() - days)
        return newDate
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
